import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Stethoscope,
  FlaskConical,
  Leaf,
  HeartPulse,
  Activity,
  UserCog,
  BriefcaseMedical,
  ShieldCheck,
  CheckCircle2,
  Download,
  PhoneCall,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import { ASSET } from "@/lib/api";
import { openingHoursToSpec, hasOpeningHours } from "@/components/OpeningHours";

const ICONS = {
  stethoscope: Stethoscope,
  "flask-conical": FlaskConical,
  leaf: Leaf,
  "heart-pulse": HeartPulse,
  activity: Activity,
  "user-cog": UserCog,
  "briefcase-medical": BriefcaseMedical,
  "shield-check": ShieldCheck,
};

const ACCENT_BG = {
  navy: "bg-navy text-white",
  teal: "bg-teal text-white",
  wellness: "bg-wellness-green text-navy",
};

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
      <span className="h-px w-6 bg-teal" />
      {children}
    </span>
  );
}

function CtaPrimary({ href, to, children, ...rest }) {
  const cls =
    "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors shadow-soft";
  return href ? (
    <a href={href} className={cls} {...rest}>{children}</a>
  ) : (
    <Link to={to} className={cls} {...rest}>{children}</Link>
  );
}

function CtaGhost({ href, to, children, ...rest }) {
  const cls =
    "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/0 ring-1 ring-white/30 text-white text-sm font-semibold hover:bg-white/10 hover:ring-white/60 transition-colors";
  return href ? (
    <a href={href} className={cls} {...rest}>{children}</a>
  ) : (
    <Link to={to} className={cls} {...rest}>{children}</Link>
  );
}

export default function Home() {
  const { data: page } = useFetch("/pages/home");
  const { data: divisions } = useFetch("/divisions");
  const { data: services } = useFetch("/services");
  const { data: settings } = useFetch("/site-settings");

  if (!page) return <div className="min-h-screen" />;

  const hero = page.hero || {};
  const outlook = page.outlook || {};
  const better = page.better_patient_care || {};

  const contact = settings?.contact || {};
  const addressFull = [contact.address_line_1, contact.address_line_2, contact.city, contact.country].filter(Boolean).join(", ");
  const hasLocalBusiness = Boolean((contact.phone || "").trim() && addressFull);

  const graph = [];
  if (settings) {
    graph.push({
      "@type": "MedicalOrganization",
      "@id": `${settings.canonical_base}/#medicalorganization`,
      name: settings.brand?.name,
      alternateName: settings.brand?.short_name,
      url: settings.canonical_base,
      logo: `${process.env.REACT_APP_BACKEND_URL}/api/media/og-image.png`,
      description: settings.seo?.default_description,
      email: settings.contact?.email || undefined,
      telephone: settings.contact?.phone || undefined,
      areaServed: { "@type": "Country", "name": "Somalia" },
      sameAs: Object.values(settings.social || {}).filter(Boolean),
    });
  }
  if (hasLocalBusiness && settings) {
    const ohSpec = openingHoursToSpec(contact.opening_hours);
    graph.push({
      "@type": "LocalBusiness",
      "@id": `${settings.canonical_base}/#localbusiness`,
      name: settings.brand?.name,
      url: settings.canonical_base,
      telephone: contact.phone,
      email: contact.email || undefined,
      address: {
        "@type": "PostalAddress",
        streetAddress: [contact.address_line_1, contact.address_line_2].filter(Boolean).join(", ") || undefined,
        addressLocality: contact.city || undefined,
        addressCountry: contact.country || "Somalia",
      },
      ...(ohSpec.length ? { openingHoursSpecification: ohSpec } : {}),
    });
  }
  const jsonLd = settings ? { "@context": "https://schema.org", "@graph": graph } : undefined;

  return (
    <>
      <SEO
        title={page.seo?.title}
        description={page.seo?.description}
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[92vh] flex items-end overflow-hidden text-white"
      >
        <div className="absolute inset-0">
          <img
            src={ASSET(hero.image)}
            alt="Infinity Medical Group"
            className="h-full w-full object-cover"
            style={{ filter: "saturate(0.85)" }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="img-grain absolute inset-0" />
        </div>

        <div className="container relative pb-20 pt-36 lg:pt-44">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.32em] text-teal-light font-semibold">
              {hero.eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              data-testid="home-hero-title"
              className="mt-6 font-display font-bold text-[40px] leading-[1.05] tracking-tight md:text-6xl lg:text-7xl max-w-4xl"
            >
              {hero.title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-white/85">
              {hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 text-xs uppercase tracking-[0.42em] text-teal-light font-semibold">
              {hero.tagline}
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-wrap gap-3">
              <CtaPrimary to="/contact" data-testid="hero-cta-contact">
                Request a consultation <ArrowUpRight className="h-4 w-4" />
              </CtaPrimary>
              <CtaGhost to="/services" data-testid="hero-cta-services">
                Explore services <ArrowUpRight className="h-4 w-4" />
              </CtaGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUTLOOK */}
      <section data-testid="home-outlook" className="bg-white py-24 lg:py-32">
        <div className="container grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>{outlook.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display font-semibold text-navy-800 text-3xl md:text-5xl leading-[1.08] tracking-tight">
                {outlook.title}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-navy-700/80">
                {outlook.body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIVISIONS */}
      <section data-testid="home-divisions" className="bg-soft-gray py-24 lg:py-32">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-5xl">
            <div>
              <Reveal>
                <Eyebrow>Three divisions, one network</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-display font-semibold text-navy-800 text-3xl md:text-5xl leading-[1.08] tracking-tight">
                  Integrated healthcare under one trusted name.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-teal transition-colors"
              >
                Explore all services <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(divisions || []).map((d, i) => {
              const Icon = ICONS[d.icon] || Stethoscope;
              return (
                <Item key={d.slug}>
                  <Link
                    to={`/divisions/${d.slug}`}
                    data-testid={`division-card-${d.slug}`}
                    className="group block rounded-3xl bg-white p-7 shadow-soft hover:shadow-card transition-shadow border border-navy-50 h-full"
                  >
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-navy-50">
                      {d.image && (
                        <img
                          src={ASSET(d.image)}
                          alt={d.name}
                          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                        />
                      )}
                    </div>
                    <div className="mt-6 flex items-center gap-2">
                      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${ACCENT_BG[d.accent] || "bg-navy text-white"}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy-500">
                        Division {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-3 font-display font-semibold text-xl text-navy-800 group-hover:text-teal transition-colors">
                      {d.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy-700/75">
                      {d.tagline}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
                      Learn more <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* BETTER PATIENT CARE / split */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-better-care">
        <div className="container grid items-center gap-14 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-16 w-16 rounded-2xl bg-teal/10" />
              <img
                src={ASSET(better.image)}
                alt="Better patient care"
                className="relative rounded-3xl shadow-card w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Better patient care</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display font-semibold text-navy-800 text-3xl md:text-4xl leading-tight tracking-tight">
                {better.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed text-navy-700/80">
                {better.body}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-teal transition-colors"
              >
                Read our story <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES TEASE */}
      <section className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden" data-testid="home-services">
        <div className="img-grain absolute inset-0" />
        <div className="container relative">
          <div className="max-w-3xl">
            <Reveal>
              <Eyebrow>Medical services</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl leading-tight tracking-tight">
                A comprehensive suite of healthcare services, delivered with care.
              </h2>
            </Reveal>
          </div>

          <Stagger className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {(services || []).map((s) => {
              const Icon = ICONS[s.icon] || HeartPulse;
              return (
                <Item key={s.slug}>
                  <div
                    data-testid={`service-card-${s.slug}`}
                    className="rounded-2xl bg-white/[0.04] border border-white/10 p-7 h-full backdrop-blur-sm hover:bg-white/[0.08] hover:border-teal/40 transition-colors"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/20 text-teal-light">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display font-semibold text-lg text-white">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/65">
                      {s.description}
                    </p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* WHY IMG */}
      <WhyChooseStrip />

      {/* FINAL CTA */}
      <section className="relative py-24 lg:py-32 bg-cream overflow-hidden" data-testid="home-final-cta">
        <div className="container">
          <div className="rounded-[2rem] bg-navy text-white p-10 lg:p-16 relative overflow-hidden">
            <div className="img-grain absolute inset-0" />
            <div className="relative grid gap-10 lg:grid-cols-12 items-center">
              <div className="lg:col-span-7">
                <Eyebrow>Get in touch</Eyebrow>
                <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl leading-tight tracking-tight">
                  Ready to experience integrated care?
                </h2>
                <p className="mt-5 text-lg text-white/75 max-w-xl">
                  Speak with our team about medical, diagnostic, pharmacy or partnership enquiries.
                  We respond within one business day.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-3">
                <Link
                  to="/contact"
                  data-testid="final-cta-contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors"
                >
                  <PhoneCall className="h-4 w-4" /> Contact our team
                </Link>
                <Link
                  to="/services"
                  data-testid="final-cta-services"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/0 ring-1 ring-white/25 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  Explore services <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function WhyChooseStrip() {
  const { data: pages } = useFetch("/pages/home");
  // shared lives on /pages/img-web - load that doc for the why-choose list.
  const { data: imgWeb } = useFetch("/pages/img-web");
  const list = imgWeb?.shared?.why_choose_us || [];
  if (!list.length) return null;
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="home-why">
      <div className="container">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Why choose IMG</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display font-semibold text-3xl md:text-5xl leading-tight tracking-tight text-navy-800">
              Trusted by patients who value precision, privacy and personal care.
            </h2>
          </Reveal>
        </div>
        <Stagger className="mt-14 grid gap-3 md:grid-cols-2 lg:grid-cols-2">
          {list.map((line, i) => (
            <Item key={line}>
              <div data-testid={`why-choose-item-${i}`} className="flex items-start gap-3 p-5 rounded-2xl bg-soft-gray border border-navy-50">
                <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                <p className="text-base font-medium text-navy-800">{line}</p>
              </div>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
